/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, NgZone} from '@angular/core';
import {ToastrService} from 'ngx-toastr';
import * as _ from 'lodash';
import template from './third-party-audience-edit-page.component.html';
import {AppEventBusService} from '../common/events/app-event-bus.service';

import {AdnSelectSearchElement} from '../common/directives/adn-select/adn-select.component';
import {AdminResourceEditPageBase} from '../common/controller/admin-resource-edit-page-base';
import {AngularModalService} from '../common/modal/angular-modal.service';
import {ThirdPartyAudiencePostcodeModalComponent} from './third-party-audience-postcode-modal.component';
import {parsePostcodeUploadText} from './third-party-audience-postcode-upload';
import {TeamResource} from '../../components/api/resources/resource/team.resource';
import {ThirdPartyAudienceResource} from '../../components/api/resources/resource/third-party-audience.resource';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {UserProfileSaverService} from '../../components/session/user-profile-saver.service';

const DEFAULT_PROVIDER = 'LC_MEDIA';

@Component({
  standalone: false,
  selector: 'adn-third-party-audience-edit-page',
  template
})
export class ThirdPartyAudienceEditPageComponent extends AdminResourceEditPageBase {
  readonly providers = ['NORDIC_DATA_RESOURCES', 'LC_MEDIA'];

  readOnly = false;
  blockSubmit: string | false = false;
  audienceData: string | null = null;
  teamSearchResults: any[] = [];

  constructor(
    eventBus: AppEventBusService,
    @Inject(UserProfileSaverService) UserProfileSaver: any,
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    @Inject(TeamResource) private readonly Team: any,
    @Inject(ThirdPartyAudienceResource) private readonly ThirdPartyAudience: any,
    private readonly toastr: ToastrService,
    private readonly angularModalService: AngularModalService,
    ngZone: NgZone
  ) {
    super(eventBus, UserProfileSaver, LocalNetworkProfile, ngZone);
  }

  protected afterInitEditingModel(): void {
    this.editingModel.provider = this.editingModel.provider || DEFAULT_PROVIDER;
    this.updateReadOnly();
    this.syncAudienceData();
  }

  protected afterInitAfterSave(): void {
    this.updateReadOnly();
    this.syncAudienceData();
  }

  canUploadPostcodes(): boolean {
    return !this.isNew && !this.readOnly && !!this.editingModel.id;
  }

  openPostcodeUploadModal(): void {
    if (!this.canUploadPostcodes()) {
      return;
    }

    const modalRef = this.angularModalService.show(ThirdPartyAudiencePostcodeModalComponent, {
      class: 'modal-lg adnDialog',
      initialState: {
        submitPostcodes: (text: string, countryIsoCode: string) => this.submitPostcodes(text, countryIsoCode)
      }
    });

    const modalContent = modalRef.content as ThirdPartyAudiencePostcodeModalComponent;
    modalContent.onClosed.subscribe(() => {
      this.ngZone.run(() => {
        this.syncAudienceData();
      });
    });
  }

  teamSearch = (searchElement: AdnSelectSearchElement): void => {
    this.Team.query({
      minimal: true,
      filterByLike: searchElement.search,
      filterBy: 'name',
      teamTypes: 'AX_ADVERTISER',
      pageSize: 100
    }).$promise.then((page: any) => {
      this.ngZone.run(() => {
        this.teamSearchResults = _.filter(page.results, (team: any) => team.type === 'AX_ADVERTISER');
      });
    });
  };

  private submitPostcodes(text: string, countryIsoCode: string): Promise<any> {
    const postcodes = parsePostcodeUploadText(text);

    if (!postcodes.length) {
      return Promise.reject(new Error('No postcodes were found in the entered text.'));
    }

    return this.ThirdPartyAudience.uploadPostcodes(this.editingModel.id, postcodes, countryIsoCode, {}).then((response: any) => {
      this.toastr.success('Postcodes uploaded successfully.', 'Third-Party Audience');
      this.audienceData = text;
      return response;
    });
  }

  private updateReadOnly(): void {
    if (this.LocalUserPermissions.hasNetworkPermission('MANAGE_SYSTEM')) {
      this.readOnly = false;
    } else if (this.isNew) {
      this.readOnly = !this.LocalUserPermissions.hasAny('MANAGE_AUDIENCES');
    } else {
      const teams = this.editingModel.teams || [];
      this.readOnly = _.isEmpty(teams) || _.some(teams, (team: any) => {
        const teamId = _.isObject(team) ? (team as any).id : team;
        return !this.LocalUserPermissions.has('MANAGE_AUDIENCES', teamId);
      });
    }
    this.blockSubmit = this.readOnly ? 'You do not have permission to update this third party audience.' : false;
  }

  private syncAudienceData(text?: string): void {
    if (text) {
      this.audienceData = text;
      return;
    }

    if (this.editingModel.provider && this.editingModel.metaData) {
      this.audienceData = _.get(this.editingModel.metaData, this.editingModel.provider) || null;
      return;
    }

    this.audienceData = null;
  }
}
